import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  chartDisplayBpm,
  chartDurationSeconds,
  formatDuration,
  modeLabel,
  type GameMode,
} from '@shared/chart';
import { deleteMap, fetchMyMaps, updateMap, type MapSummary } from '../apiClient/maps';
import { modePath } from '../game/modeRouting';
import './myMaps.css';

interface MyMapsListProps {
  /**
   * Restrict the list to a single mode. Pass the builder's mode so the
   * "edit an existing map" picker only offers maps you can open in *this*
   * editor; omit on the profile to show the player's full cross-mode
   * library.
   */
  mode?: GameMode;
  /**
   * 'profile' renders the full library section (heading, count, create
   * CTAs, empty state). 'builder' renders a compact picker that collapses
   * to nothing while loading or when the player has no maps for the mode,
   * so a first-time creator's editor stays uncluttered.
   */
  variant?: 'profile' | 'builder';
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; maps: MapSummary[] };

// Only Pulse and Guitar have in-app editors today; the profile's "create"
// affordances link straight into those two builders.
const CREATE_MODES: GameMode[] = ['pulse', 'guitar'];

// A map is only editable in a mode that ships an in-app builder. The
// profile library is cross-mode, so the per-row "Edit" link must be gated
// on this rather than assuming every owned map has a `/build/:slug` route.
function hasEditor(mode: GameMode): boolean {
  return CREATE_MODES.includes(mode);
}

export function MyMapsList({ mode, variant = 'profile' }: MyMapsListProps) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const all = await fetchMyMaps();
      const maps = mode ? all.filter((m) => m.mode === mode) : all;
      setState({ status: 'ready', maps });
    } catch (err) {
      console.error('Failed to load your maps:', err);
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Failed to load your maps',
      });
    }
  }, [mode]);

  useEffect(() => {
    void load();
  }, [load]);

  const onToggleVisibility = async (m: MapSummary) => {
    setBusyId(m.id);
    setActionError(null);
    try {
      const result = await updateMap(m.id, { isPublic: !m.isPublic });
      setState((prev) =>
        prev.status === 'ready'
          ? {
              status: 'ready',
              maps: prev.maps.map((x) =>
                x.id === m.id ? { ...x, isPublic: result.isPublic } : x,
              ),
            }
          : prev,
      );
    } catch (err) {
      console.error('Toggle map visibility failed:', err);
      setActionError(err instanceof Error ? err.message : 'Could not update visibility');
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (m: MapSummary) => {
    if (!window.confirm(`Delete "${m.title}"? This can't be undone.`)) {
      return;
    }
    setBusyId(m.id);
    setActionError(null);
    try {
      await deleteMap(m.id);
      setState((prev) =>
        prev.status === 'ready'
          ? { status: 'ready', maps: prev.maps.filter((x) => x.id !== m.id) }
          : prev,
      );
    } catch (err) {
      console.error('Delete map failed:', err);
      setActionError(err instanceof Error ? err.message : 'Could not delete map');
    } finally {
      setBusyId(null);
    }
  };

  // The builder picker is a pure convenience: stay invisible until we
  // actually have something worth offering so a fresh editor isn't
  // cluttered with spinners or empty-states.
  if (variant === 'builder' && (state.status !== 'ready' || state.maps.length === 0)) {
    return null;
  }

  const heading =
    variant === 'builder' && mode ? `Or edit one of your ${modeLabel(mode)} maps` : 'Your maps';

  return (
    <section
      className={`my-maps my-maps-${variant}`}
      data-testid={mode ? `my-maps-${mode}` : 'my-maps'}
    >
      <header className="my-maps-header">
        <div>
          <h3>{heading}</h3>
          {state.status === 'ready' && variant === 'profile' && (
            <p className="muted faint my-maps-count">
              {state.maps.length === 0
                ? 'Nothing here yet'
                : `${state.maps.length} map${state.maps.length === 1 ? '' : 's'}`}
            </p>
          )}
        </div>
        {variant === 'profile' && (
          <div className="my-maps-create">
            {CREATE_MODES.map((m) => (
              <Link key={m} to={modePath(m, 'build')} className="btn btn-ghost">
                New {modeLabel(m)} map
              </Link>
            ))}
          </div>
        )}
      </header>

      {actionError && <div className="error-box">{actionError}</div>}

      {state.status === 'loading' && <p className="muted">Loading your maps...</p>}

      {state.status === 'error' && (
        <div className="my-maps-error">
          <p className="muted">{state.message}</p>
          <button type="button" className="btn btn-ghost" onClick={() => void load()}>
            Try again
          </button>
        </div>
      )}

      {state.status === 'ready' && state.maps.length === 0 && variant === 'profile' && (
        <p className="muted my-maps-empty">
          You haven&apos;t made any maps yet. Build one in the Pulse or Guitar editor and it&apos;ll
          show up here — public or private.
        </p>
      )}

      {state.status === 'ready' && state.maps.length > 0 && (
        <ul className="my-maps-list">
          {state.maps.map((m) => (
            <li
              key={m.id}
              className="my-map-row"
              data-testid={`my-map-${m.mode}-${m.slug}`}
              style={{ '--map-pad': m.palette.pad } as React.CSSProperties}
            >
              <div className="my-map-main">
                <div className="my-map-titleline">
                  <Link to={modePath(m.mode, `play/${m.slug}`)} className="my-map-title">
                    {m.title}
                  </Link>
                  {!mode && <span className="my-map-mode">{modeLabel(m.mode)}</span>}
                  <span
                    className={`my-map-status ${m.isPublic ? 'is-public' : 'is-private'}`}
                    title={
                      m.isPublic
                        ? 'Anyone can find this in Browse'
                        : 'Only you can see this — make it public to share'
                    }
                  >
                    {m.isPublic ? 'Public' : 'Private'}
                  </span>
                </div>
                <div className="my-map-meta">
                  <span>{chartDisplayBpm(m.bpm).toFixed(0)} BPM</span>
                  <span>{formatDuration(chartDurationSeconds(m.bpm, m.totalBeats))}</span>
                  <span className="diff-pips" aria-label={`Difficulty ${m.difficulty} of 5`}>
                    {Array.from({ length: 5 }, (_, i) => (
                      <span key={i} className={i < m.difficulty ? 'pip pip-on' : 'pip'} />
                    ))}
                  </span>
                  <span>{m.playCount.toLocaleString()} plays</span>
                  <span title="Likes">
                    {'\u2665'} {m.likeCount.toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="my-map-actions">
                {hasEditor(m.mode) && (
                  <Link
                    to={modePath(m.mode, `build/${m.slug}`)}
                    className="btn btn-primary"
                    data-testid={`my-map-edit-${m.slug}`}
                  >
                    Edit
                  </Link>
                )}
                <Link to={modePath(m.mode, `play/${m.slug}`)} className="btn btn-ghost">
                  Play
                </Link>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => void onToggleVisibility(m)}
                  disabled={busyId === m.id}
                >
                  {m.isPublic ? 'Make private' : 'Make public'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost my-map-delete"
                  onClick={() => void onDelete(m)}
                  disabled={busyId === m.id}
                  data-testid={`my-map-delete-${m.slug}`}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
